import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  makeStyles,
  LinearProgress,
  CircularProgress,
} from '@material-ui/core';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 12,
      maxWidth: 420,
      padding: theme.spacing(1),
    },
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    borderRadius: '50%',
    backgroundColor: '#FFF3E0',
  },
  icon: {
    fontSize: 28,
    color: '#F57C00',
  },
  title: {
    fontFamily: 'IBM Plex Sans, sans-serif',
    fontWeight: 600,
    fontSize: 20,
    color: '#1D3557',
  },
  content: {
    padding: theme.spacing(2, 3),
  },
  message: {
    fontFamily: 'IBM Plex Sans, sans-serif',
    fontSize: 15,
    color: '#555',
    marginBottom: theme.spacing(3),
    lineHeight: 1.6,
  },
  timerContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(2),
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    marginBottom: theme.spacing(2),
  },
  timerLabel: {
    fontFamily: 'IBM Plex Sans, sans-serif',
    fontSize: 13,
    color: '#666',
    marginBottom: theme.spacing(1),
  },
  timer: {
    fontFamily: 'IBM Plex Mono, monospace',
    fontSize: 36,
    fontWeight: 700,
    color: '#D32F2F',
    letterSpacing: 2,
  },
  expiredContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  expiredIcon: {
    fontSize: 48,
    color: '#D32F2F',
  },
  expiredText: {
    fontFamily: 'IBM Plex Sans, sans-serif',
    fontSize: 18,
    fontWeight: 600,
    color: '#D32F2F',
  },
  redirectingText: {
    fontFamily: 'IBM Plex Sans, sans-serif',
    fontSize: 16,
    fontWeight: 500,
    color: '#1D3557',
    marginTop: 8,
  },
  progressBar: {
    width: '100%',
    height: 6,
    borderRadius: 3,
    marginTop: theme.spacing(1.5),
    backgroundColor: '#E0E0E0',
    '& .MuiLinearProgress-bar': {
      borderRadius: 3,
      backgroundColor: '#F57C00',
    },
  },
  actions: {
    padding: theme.spacing(2, 3, 3),
    gap: theme.spacing(2),
    justifyContent: 'center',
  },
  logoutButton: {
    fontFamily: 'IBM Plex Sans, sans-serif',
    fontSize: 14,
    fontWeight: 500,
    textTransform: 'none',
    padding: theme.spacing(1, 3),
    borderRadius: 6,
    minHeight: 40,
    color: '#666',
    borderColor: '#DDD',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#F5F5F5',
      borderColor: '#CCC',
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      color: '#BDBDBD',
      borderColor: '#E0E0E0',
    },
  },
  stayButton: {
    fontFamily: 'IBM Plex Sans, sans-serif',
    fontSize: 14,
    fontWeight: 600,
    textTransform: 'none',
    padding: theme.spacing(1, 3),
    borderRadius: 6,
    minHeight: 40,
    background: 'linear-gradient(90deg, #3B0089 0%, #004AB3 100%)',
    color: 'white',
    boxShadow: 'none !important',
    '&:hover': {
      background: 'linear-gradient(90deg, #2D0066 0%, #003A8C 100%)',
      boxShadow: 'none !important',
    },
    '&:active': {
      boxShadow: 'none !important',
    },
    '&:focus': {
      boxShadow: 'none !important',
    },
    '&.Mui-disabled': {
      background: '#9E9E9E',
      color: 'rgba(255, 255, 255, 0.7)',
    },
  },
  buttonContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

interface SessionWarningModalProps {
  open: boolean;
  secondsRemaining: number;
  isAbsoluteExpiry?: boolean;
  onStayLoggedIn: () => void;
  onLogout: () => void;
  onReLogin?: () => void;
  onCancel?: () => void;
}

const SessionWarningModal: React.FC<SessionWarningModalProps> = ({
  open,
  secondsRemaining,
  isAbsoluteExpiry = false,
  onStayLoggedIn,
  onLogout,
  onReLogin,
  onCancel,
}) => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);

  // Reset loading state when modal closes
  useEffect(() => {
    if (!open) {
      setIsLoading(false);
    }
  }, [open]);

  const handleReLogin = () => {
    setIsLoading(true);
    onReLogin?.();
  };

  // Format seconds as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate progress (starts at 100%, goes to 0%)
  const sessionTimeout = Number(process.env.NEXT_PUBLIC_AUTH0_SESSION_ROLLING_DURATION) || 1800;
  const absoluteTimeout = Number(process.env.NEXT_PUBLIC_AUTH0_SESSION_ABSOLUTE_DURATION) || 10800;

  // Use appropriate threshold based on expiry type
  const totalSeconds = isAbsoluteExpiry
    ? Math.max(60, Math.min(Math.floor(absoluteTimeout / 10), 900)) // 10% of absolute, min 1 min, max 15 min
    : Math.min(Math.floor(sessionTimeout / 2), 120); // Half of rolling, max 2 min

  const progress = (secondsRemaining / totalSeconds) * 100;
  const isExpired = secondsRemaining === 0;

  // Determine title and message based on state
  const getTitle = () => {
    if (isExpired) return 'Session Expired';
    return isAbsoluteExpiry ? 'Session Ending Soon' : 'Session Expiring Soon';
  };

  const getMessage = () => {
    if (isExpired) {
      return 'Your session has expired. Please re-login to continue using the application.';
    }
    return isAbsoluteExpiry
      ? 'Your session is ending soon for security reasons. Please save your work and re-login to continue.'
      : 'Your session is about to expire due to inactivity. Would you like to stay logged in?';
  };

  const getTimerLabel = () => {
    if (isExpired) return 'Session expired';
    return isAbsoluteExpiry ? 'Session ends in' : 'Session expires in';
  };

  return (
    <Dialog
      open={open}
      className={classes.dialog}
      onClose={(event, reason) => {
        // Prevent closing by backdrop click or escape key
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
      }}
      aria-labelledby="session-warning-dialog-title"
    >
      <DialogTitle disableTypography>
        <Box className={classes.titleContainer}>
          <Box className={classes.iconWrapper}>
            <AccessTimeIcon className={classes.icon} />
          </Box>
          <Typography className={classes.title} id="session-warning-dialog-title">
            {getTitle()}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <Typography className={classes.message}>{getMessage()}</Typography>

        <Box className={classes.timerContainer}>
          {isLoading ? (
            <Box className={classes.expiredContainer}>
              <CircularProgress size={40} style={{ color: '#3B0089' }} />
              <Typography className={classes.redirectingText}>Redirecting to login...</Typography>
            </Box>
          ) : isExpired ? (
            <Box className={classes.expiredContainer}>
              <ErrorOutlineIcon className={classes.expiredIcon} />
              <Typography className={classes.expiredText}>Session Expired</Typography>
            </Box>
          ) : (
            <>
              <Typography className={classes.timerLabel}>{getTimerLabel()}</Typography>
              <Typography className={classes.timer}>{formatTime(secondsRemaining)}</Typography>
              <LinearProgress variant="determinate" value={progress} className={classes.progressBar} />
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions className={classes.actions}>
        {!isExpired && (
          <Button variant="outlined" onClick={onCancel} className={classes.logoutButton} disabled={isLoading}>
            Cancel
          </Button>
        )}
        {isAbsoluteExpiry ? (
          <Button variant="contained" onClick={handleReLogin} className={classes.stayButton} disabled={isLoading}>
            <span className={classes.buttonContent}>
              {isLoading && <CircularProgress size={16} color="inherit" />}
              Re-login Now
            </span>
          </Button>
        ) : (
          <Button variant="contained" onClick={onStayLoggedIn} className={classes.stayButton}>
            Stay Logged In
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SessionWarningModal;
